import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  Sparkles, Plus, TrendingDown, TrendingUp, History, 
  AlertTriangle, CreditCard, BarChart3, CheckCircle, Wifi, WifiOff
} from 'lucide-react';
import api from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const AICreditsManagement = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
  const token = localStorage.getItem('movemate_token');
  const [searchParams, setSearchParams] = useSearchParams();
  const [credits, setCredits] = useState({ balance: 0, used: 0, low_threshold: 10 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(50);
  const [confirmingPurchase, setConfirmingPurchase] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!user.company_id || !token || !WS_URL) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const wsUrl = `${WS_URL}/ws/credits/${user.company_id}?token=${token}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('Credits WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'balance_update') {
            // Update balance in real-time
            setCredits(prev => ({
              ...prev,
              balance: data.balance
            }));
            
            // Show toast for changes
            if (data.change !== undefined) {
              if (data.change > 0) {
                toast.success(`+${data.change} credits added! ${data.description || ''}`);
              } else if (data.change < 0) {
                toast.info(`${data.change} credits used${data.description ? `: ${data.description}` : ''}`);
              }
            }
            
            // Refresh transaction history
            fetchTransactions();
          } else if (data.type === 'connected') {
            // Initial balance from connection
            setCredits(prev => ({
              ...prev,
              balance: data.balance
            }));
          }
        } catch (e) {
          console.error('Error parsing WS message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        console.log('Credits WebSocket disconnected');
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.company_id, token]);

  // Connect WebSocket on mount
  useEffect(() => {
    connectWebSocket();
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Keep WebSocket alive with ping
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTransactions = async () => {
    try {
      const historyRes = await api.get(`/companies/${user.company_id}/credits/history`);
      setTransactions(historyRes.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }
  };

  useEffect(() => {
    // Check for successful payment return
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const sessionId = searchParams.get('session_id');
    const creditsParam = searchParams.get('credits');
    
    if (success === 'true') {
      // Try to confirm purchase with backend (backup for webhook)
      if (sessionId) {
        confirmPurchase(sessionId, creditsParam);
      } else {
        toast.success(`Successfully purchased ${creditsParam || ''} credits!`);
        // Clear URL params
        setSearchParams({});
        fetchData();
      }
    } else if (cancelled === 'true') {
      toast.info('Purchase cancelled');
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const confirmPurchase = async (sessionId, creditsParam) => {
    setConfirmingPurchase(true);
    try {
      const token = localStorage.getItem('movemate_token');
      const response = await fetch(`${API_URL}/api/companies/${user.company_id}/credits/confirm-purchase?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Successfully added ${data.credits_added} credits! New balance: ${data.new_balance}`);
      } else {
        // If already confirmed, that's OK
        if (data.detail?.includes('already confirmed')) {
          toast.success(`Credits already added!`);
        } else {
          toast.error(data.detail || 'Failed to confirm purchase');
        }
      }
    } catch (error) {
      // If confirmation fails, the webhook should have handled it
      toast.success(`Successfully purchased ${creditsParam || ''} credits!`);
    } finally {
      setConfirmingPurchase(false);
      setSearchParams({});
      fetchData();
    }
  };

  const fetchData = async () => {
    try {
      const [creditsRes, historyRes] = await Promise.all([
        api.get(`/companies/${user.company_id}/credits`),
        api.get(`/companies/${user.company_id}/credits/history`)
      ]);
      setCredits(creditsRes.data);
      setTransactions(historyRes.data);
    } catch (error) {
      toast.error('Failed to load credit data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setLoading(true);
      // Create Stripe checkout session for credit purchase
      const response = await api.post(`/companies/${user.company_id}/credits/checkout`, {
        amount: purchaseAmount,
        price: (purchaseAmount * 0.36).toFixed(2)
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setLoading(false);
    }
  };

  const creditPacks = [
    { amount: 25, price: '£9.99', popular: false },
    { amount: 50, price: '£17.99', popular: true },
    { amount: 100, price: '£29.99', popular: false },
    { amount: 250, price: '£64.99', popular: false },
  ];

  const isLowCredits = credits.balance < credits.low_threshold;
  const usagePercent = credits.used > 0 ? Math.min((credits.used / (credits.used + credits.balance)) * 100, 100) : 0;

  return (
    <div className="space-y-6" data-testid="ai-credits-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">AI Credits</h1>
          <div className="flex items-center gap-2">
            <p className="text-gray-600">Manage your AI feature usage and credits</p>
            {wsConnected ? (
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                <Wifi size={12} className="mr-1" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-500">
                <WifiOff size={12} className="mr-1" /> Offline
              </Badge>
            )}
          </div>
        </div>
        <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
          <DialogTrigger asChild>
            <Button data-testid="purchase-credits-btn">
              <Plus size={20} className="mr-2" />
              Purchase Credits
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Purchase AI Credits</DialogTitle>
              <DialogDescription>
                Select a credit pack to power your AI features
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {creditPacks.map((pack) => (
                  <Button
                    key={pack.amount}
                    variant={purchaseAmount === pack.amount ? 'default' : 'outline'}
                    className={`h-24 flex-col relative ${pack.popular ? 'border-primary' : ''}`}
                    onClick={() => setPurchaseAmount(pack.amount)}
                  >
                    {pack.popular && (
                      <Badge className="absolute -top-2 -right-2 text-xs">Popular</Badge>
                    )}
                    <span className="text-2xl font-bold">{pack.amount}</span>
                    <span className="text-sm">credits</span>
                    <span className="text-xs text-gray-500">{pack.price}</span>
                  </Button>
                ))}
              </div>

              <div>
                <Label>Or enter custom amount</Label>
                <Input
                  type="number"
                  min="1"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(parseInt(e.target.value) || 1)}
                  className="mt-2"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Credits:</span>
                  <span className="font-semibold">{purchaseAmount}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Price:</span>
                  <span className="font-semibold">
                    £{(purchaseAmount * 0.36).toFixed(2)}
                  </span>
                </div>
              </div>

              <Button onClick={handlePurchase} className="w-full" data-testid="confirm-purchase">
                <CreditCard size={18} className="mr-2" />
                Purchase {purchaseAmount} Credits
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Credits never expire. Use them for AI inventory scanning, image recognition, and more.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Low Credits Warning */}
      {isLowCredits && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-orange-500" size={32} />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">Low Credit Balance</h3>
                <p className="text-sm text-orange-700">
                  You have only {credits.balance} credits remaining. Purchase more to continue using AI features.
                </p>
              </div>
              <Button onClick={() => setShowPurchaseDialog(true)} className="bg-orange-500 hover:bg-orange-600">
                Top Up Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Current Balance</p>
                <p className="text-4xl font-bold text-primary">{credits.balance}</p>
                <p className="text-xs text-gray-500 mt-1">credits available</p>
              </div>
              <Sparkles className="text-primary" size={40} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Used</p>
                <p className="text-4xl font-bold text-blue-600">{credits.used}</p>
                <p className="text-xs text-gray-500 mt-1">credits consumed</p>
              </div>
              <TrendingDown className="text-blue-600" size={40} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Usage Rate</p>
                <p className="text-4xl font-bold text-green-600">{usagePercent.toFixed(0)}%</p>
                <p className="text-xs text-gray-500 mt-1">of total purchased</p>
              </div>
              <BarChart3 className="text-green-600" size={40} />
            </div>
            <Progress value={usagePercent} className="mt-3 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* AI Features & Costs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={20} />
            AI Feature Costs
          </CardTitle>
          <CardDescription>Credit usage per AI feature</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
              <div>
                <h4 className="font-semibold">Inventory Scanner</h4>
                <p className="text-sm text-gray-500">AI-powered item detection</p>
              </div>
              <Badge variant="outline" className="text-lg font-bold">1 credit</Badge>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
              <div>
                <h4 className="font-semibold">Room Analysis</h4>
                <p className="text-sm text-gray-500">Full room inventory detection</p>
              </div>
              <Badge variant="outline" className="text-lg font-bold">5 credits</Badge>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
              <div>
                <h4 className="font-semibold">Video Walkthrough</h4>
                <p className="text-sm text-gray-500">AI video analysis for inventory</p>
              </div>
              <Badge variant="outline" className="text-lg font-bold">10 credits</Badge>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
              <div>
                <h4 className="font-semibold">Smart Estimate</h4>
                <p className="text-sm text-gray-500">AI-generated moving estimate</p>
              </div>
              <Badge variant="outline" className="text-lg font-bold">3 credits</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History size={20} />
            Transaction History
          </CardTitle>
          <CardDescription>Recent credit purchases and usage</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <History className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No transactions yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowPurchaseDialog(true)}
              >
                Purchase Your First Credits
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div 
                  key={tx.id}
                  className="flex justify-between items-center p-4 border rounded-lg"
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-center gap-4">
                    {tx.transaction_type === 'purchase' ? (
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="text-green-600" size={20} />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <TrendingDown className="text-blue-600" size={20} />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{tx.description}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString()} at {new Date(tx.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </p>
                    <p className="text-xs text-gray-500">
                      Balance: {tx.balance_after}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AICreditsManagement;
